import _ from 'lodash';
import { Amount, PaymentStatusType } from '../../api';
import { parseAmount } from '../../services';
import { getPaymentIndex } from '../services';

export interface ClaimPaymentSummaryItem {
    id?: string;
    status?: PaymentStatusType | string;
    created?: string;
    allowedAmount?: Amount;
    payerAmount?: Amount;
    chargeAmount?: Amount;
    patientAmount?: Amount;
}

export function getLatestPaymentsByPayerPriority<T extends ClaimPaymentSummaryItem>(payments: T[]): T[] {
    const primaries = _.filter(payments, payment => getPaymentIndex(payment.status as PaymentStatusType) === 0);
    const secondaries = _.filter(payments, payment => getPaymentIndex(payment.status as PaymentStatusType) === 1);
    const tertiaries = _.filter(payments, payment => getPaymentIndex(payment.status as PaymentStatusType) === 2);

    return tertiaries.length ? tertiaries : secondaries.length ? secondaries : primaries;
}

export function getReversedPayment<T extends ClaimPaymentSummaryItem>(reversal: T, payments: T[]): T | undefined {
    const comparingAmounts: Array<keyof Pick<T, 'allowedAmount' | 'payerAmount' | 'chargeAmount' | 'patientAmount'>> = [
        'allowedAmount',
        'payerAmount',
        'chargeAmount',
        'patientAmount',
    ];

    const reversedPayments = payments.filter(payment =>
        comparingAmounts.every(key => {
            const reversalAmount = reversal[key];
            const paymentAmount = payment[key];
            if (!reversalAmount || !paymentAmount) {
                return false;
            }

            return Math.abs(parseAmount(reversalAmount)) === Math.abs(parseAmount(paymentAmount));
        })
    );

    return _.first(reversedPayments);
}

export function getPaymentsWithoutReversals<T extends ClaimPaymentSummaryItem>(payments: T[] = []): T[] {
    const getTimestamp = (created?: string): number => {
        if (!created) {
            return Number.NEGATIVE_INFINITY;
        }

        const timestamp = new Date(created).getTime();
        return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
    };

    const sortedPayments = _(payments)
        .slice()
        .sort((a, b) => {
            const aTs = getTimestamp(a.created);
            const bTs = getTimestamp(b.created);
            if (aTs === bTs) {
                return 0;
            }

            return bTs - aTs;
        })
        .value();

    const paymentReversals = sortedPayments.filter(
        payment => payment.status === PaymentStatusType.REVERSALOFPREVIOUSPAYMENT
    );
    const nonReversalPayments = sortedPayments.filter(
        payment => payment.status !== PaymentStatusType.REVERSALOFPREVIOUSPAYMENT
    );

    paymentReversals.forEach(reversal => {
        const reversedPayment = getReversedPayment(reversal, nonReversalPayments);
        _.pull(nonReversalPayments, reversedPayment);
    });

    return nonReversalPayments;
}
